import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { ArrowUp, MessageCircle, Plus, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = {
  domande: "Domande",
  idee: "Idee",
  esperienze: "Esperienze",
  scambi: "Scambi",
};

const CAT_COLOR = {
  domande: "#C05746",
  idee: "#79845B",
  esperienze: "#2D4A22",
  scambi: "#8b5a3c",
};

const formatTime = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};

export default function Forum() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("recent");
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const r = await axios.get(`${API}/forum`, { params: { ...(cat ? { category: cat } : {}), sort } });
    setPosts(r.data);
  };

  useEffect(() => { load(); }, [cat, sort]);

  const upvote = async (id) => {
    if (!user) { toast.info("Accedi per votare"); return; }
    try {
      await axios.post(`${API}/forum/${id}/upvote`);
      load();
    } catch (e) { console.error("Upvote failed:", e); }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="forum-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-3">Voci che si cercano</div>
          <h1 className="font-serif text-5xl md:text-6xl text-tc-primary">Forum</h1>
          <p className="text-tc-muted mt-4 max-w-xl">
            Domande, idee, esperienze, scambi. Lo spazio dove la comunità si parla davvero.
          </p>
        </div>
        {user ? (
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 self-start" data-testid="new-post-btn">
            <Plus className="w-4 h-4" /> Apri una conversazione
          </button>
        ) : (
          <Link to="/login" className="btn-outline self-start">Accedi per scrivere</Link>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2" data-testid="forum-filters">
          <Chip active={!cat} onClick={() => setCat("")} label="Tutto" />
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <Chip key={k} active={cat === k} onClick={() => setCat(k)} label={v} />
          ))}
        </div>
        <div className="flex gap-2 text-xs">
          <button onClick={() => setSort("recent")} className={`px-3 py-1 rounded-full ${sort === 'recent' ? 'bg-tc-primary text-tc-bg' : 'text-tc-muted'}`} data-testid="sort-recent">Recenti</button>
          <button onClick={() => setSort("top")} className={`px-3 py-1 rounded-full ${sort === 'top' ? 'bg-tc-primary text-tc-bg' : 'text-tc-muted'}`} data-testid="sort-top">Più votati</button>
        </div>
      </div>

      <div className="space-y-3" data-testid="forum-list">
        {posts.length === 0 && <p className="text-tc-muted italic py-12 text-center font-serif text-xl">Il silenzio è già una risposta. Scrivi la prima parola.</p>}
        {posts.map(p => (
          <article key={p.id} className="bg-tc-surface rounded-2xl border border-tc/40 hover:border-tc-primary/50 transition-colors flex" data-testid={`post-${p.id}`}>
            <div className="flex flex-col items-center justify-start py-4 px-3 border-r border-tc/30 min-w-[60px]">
              <button onClick={() => upvote(p.id)} className="group" data-testid={`upvote-${p.id}`}>
                <ArrowUp className={`w-5 h-5 transition-colors ${p.upvoted_by?.includes(user?.user_id) ? 'text-tc-secondary' : 'text-tc-muted group-hover:text-tc-primary'}`} />
              </button>
              <span className="text-sm font-medium text-tc-primary mt-1">{p.upvote_count || 0}</span>
            </div>
            <Link to={`/forum/${p.id}`} className="flex-1 p-5">
              <div className="flex items-center gap-3 text-xs text-tc-muted mb-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full uppercase tracking-wider text-[10px]"
                  style={{ background: `${CAT_COLOR[p.category]}20`, color: CAT_COLOR[p.category] }}>
                  {CATEGORIES[p.category]}
                </span>
                <span>{p.author_name}</span>
                <span>·</span>
                <span>{formatTime(p.created_at)}</span>
              </div>
              <h3 className="font-serif text-2xl text-tc-primary leading-tight mb-1">{p.title}</h3>
              <p className="text-sm text-tc-muted line-clamp-2">{p.body}</p>
              <div className="flex items-center gap-1 text-xs text-tc-muted mt-3">
                <MessageCircle className="w-3 h-3" /> {p.reply_count || 0} risposte
              </div>
            </Link>
          </article>
        ))}
      </div>

      {showForm && <NewPostModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </main>
  );
}

function Chip({ active, onClick, label }) {
  return (
    <button onClick={onClick} data-testid={`f-filter-${label}`}
      className={`px-4 py-1.5 rounded-full text-sm transition-colors border ${active ? 'bg-tc-primary text-tc-bg border-tc-primary' : 'text-tc-muted border-tc hover:border-tc-primary'}`}>
      {label}
    </button>
  );
}

function NewPostModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", body: "", category: "domande" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/forum`, form);
      toast.success("Conversazione aperta");
      onSaved();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-tc-primary/40 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="new-post-modal">
      <div className="bg-tc-bg rounded-3xl max-w-xl w-full p-8 border border-tc relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-tc-muted hover:text-tc-primary" data-testid="close-post-modal"><X /></button>
        <h2 className="font-serif text-3xl text-tc-primary mb-6">Apri una conversazione</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Categoria</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="post-cat">
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Titolo</label>
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="post-title" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Il tuo pensiero</label>
            <textarea required rows={6} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none" data-testid="post-body" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="post-submit">
            {busy ? "Pubblico…" : "Pubblica"}
          </button>
        </form>
      </div>
    </div>
  );
}
